import { SendHorizonalIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLayoutEffect, useRef } from 'react';
import { Box, Flex, Form, IconButton, TextArea } from '@/components/common';

const MIN_INPUT_HEIGHT = 40;
const MAX_INPUT_HEIGHT = 120;

interface ChatInputProps {
  draft: string;
  setDraft: (value: string) => void;
  onSubmit: (e: { preventDefault: () => void }) => void;
  canSend: boolean;
}

export function ChatInput({ draft, setDraft, onSubmit, canSend }: ChatInputProps) {
  const { t } = useTranslation('chat');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const singleLineHeightRef = useRef(MIN_INPUT_HEIGHT);

  useLayoutEffect(() => {
    const input = inputRef.current;
    const root = input?.parentElement;
    if (!input || !root) return;

    root.style.height = 'auto';
    input.style.height = 'auto';

    const contentHeight = Math.max(input.scrollHeight, MIN_INPUT_HEIGHT);
    if (!draft) {
      singleLineHeightRef.current = contentHeight;
    }

    const singleLineHeight = singleLineHeightRef.current;
    const height = Math.min(contentHeight, MAX_INPUT_HEIGHT);
    const expanded = contentHeight > singleLineHeight + 1;

    input.style.height = `${height}px`;
    input.style.overflowY = contentHeight > MAX_INPUT_HEIGHT ? 'auto' : 'hidden';

    root.style.height = `${height}px`;
    root.style.borderRadius = expanded ? 'var(--radius-pill)' : 'var(--radius-full-pill)';
  }, [draft]);

  return (
    <Flex asChild align="end" gap="2" px="5" py="3" flexShrink="0">
      <Form.Root onSubmit={onSubmit}>
        <Box flexGrow="1" flexBasis="0">
          <Form.Field name="message">
            <TextArea
              ref={inputRef}
              variant="classic"
              radius="full"
              size="2"
              name="message"
              rows={1}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder={t('dm.placeholder')}
              autoComplete="off"
              style={{
                minHeight: MIN_INPUT_HEIGHT,
                maxHeight: MAX_INPUT_HEIGHT,
                boxSizing: 'border-box',
                borderRadius: 'var(--radius-full-pill)',
                overflowY: 'hidden',
              }}
            />
          </Form.Field>
        </Box>
        <IconButton
          type="submit"
          size="3"
          variant="ghost"
          highContrast
          radius="full"
          disabled={!canSend}
          aria-label={t('dm.sendA11y')}
        >
          <SendHorizonalIcon size="26" />
        </IconButton>
      </Form.Root>
    </Flex>
  );
}
